import type { Timesheet, TimesheetListItem } from '@tabel/shared/schemas/timesheets';
import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { type FormEvent, useEffect, useId, useRef, useState } from 'react';
import { type Brigade, type CreateBody, type TimesheetOverlap, api, overlapsOf } from '../lib/api';
import { ApiError, isTransient, reportClientError } from '../lib/errors';
import { STATUS_LABEL, ruDate } from '../lib/format';
import { plural } from '../lib/questions';
import { type ToastAction, useToast } from './ErrorToast';
import { nameList, people } from './PeoplePanel';
import { Button, Callout, Field, Modal, inputCls } from './ui';
import { useUndo } from './useUndo';

const SCREEN = 'BrigadierList';
const MAX_DAYS = 31;
const DAY_MS = 86_400_000;
/** Пауза перед проверкой пересечения, пока вводят название бригады. */
const CHECK_DELAY_MS = 300;

const span = (a: string, b: string) =>
  Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / DAY_MS) + 1;
const days = (n: number) => `${n} ${plural(n, ['день', 'дня', 'дней'])}`;
const short = (iso: string) => ruDate(iso).slice(0, 5);
const range = (a: string, b: string) => `${short(a)} — ${short(b)}`;
const statusText = (s: string) =>
  (Object.hasOwn(STATUS_LABEL, s) ? STATUS_LABEL[s as keyof typeof STATUS_LABEL] : s).toLowerCase();

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/**
 * Состав нового табеля изменился вне его страницы (копирование и «Отменить» в тосте, 003 FR-038): страница табеля уже
 * открыта — табель, вопросы, расчёт дня, сводка перед «Завершить», кандидаты и предпросмотр копирования перечитываются,
 * как после действий на самой странице; список табелей и сводка при входе — тоже. Табель из ответа отмены — сразу.
 */
function refreshTimesheet(qc: QueryClient, id: number, ts?: Timesheet) {
  if (ts) qc.setQueriesData<Timesheet>({ queryKey: ['timesheet', id] }, ts);
  for (const queryKey of [
    ['timesheet', id],
    ['questions', id],
    ['calc', id],
    ['summary', id],
    ['candidates', id],
    ['copy-preview', id],
    ['timesheets'],
    ['questions', 'summary'],
  ]) {
    void qc.invalidateQueries({ queryKey });
  }
}

/**
 * Состав табеля-источника: ФИО для «Что будет» и тоста о пропущенных (тот же ключ, что в «Добавить людей»). Не
 * загрузился — окно обходится числом людей, а сбой уходит в журнал (T126): одна запись на каждый неудачный запрос.
 */
const sourceQuery = (id: number | null) => ({
  queryKey: ['copy-source', id],
  queryFn: async () => {
    try {
      return await api.get(id as number);
    } catch (err) {
      reportClientError(SCREEN, 'copySource', err);
      throw err;
    }
  },
  staleTime: 60_000,
});

/** Проверка дат: ошибка (кнопка неактивна) или подсказка — сколько дней и начался ли период. */
function checkDates(start: string, end: string, today: string): { error: string | null; hint: string } {
  if (!start || !end) return { error: null, hint: `Период — не длиннее ${MAX_DAYS} дня.` };
  if (start > end) return { error: 'Начало позже конца — поменяйте даты местами', hint: '' };
  const n = span(start, end);
  if (n > MAX_DAYS) return { error: `Период ${days(n)} — не больше ${MAX_DAYS}`, hint: '' };
  return {
    error: null,
    hint: `${days(n)}: ${short(start)} — ${ruDate(end)}.${start > today ? ' Период ещё не начался.' : ''}`,
  };
}

/**
 * Окно «Новый табель» (003 US3, сценарии 1–2; FR-032, FR-033; contracts/ui-panel.md, «Окна»; прототип, Ж-01):
 * - бригада: у бригадира несколько бригад — выбор; одна — показана без выбора; нет (`/api/me` без бригад) —
 *   «У вас нет бригады на объекте — обратитесь к администратору», полей нет, «Создать табель» неактивна (T131):
 *   сервер создаёт табель только в своей бригаде;
 * - даты: начало не позже конца, период не длиннее 31 дня — причина под датами, «Создать табель» неактивна;
 * - пересечение с табелем той же бригады: сервер проверяет заранее (`dryRun`) и при создании (`409 PERIOD_OVERLAP`);
 *   окно показывает такие табели, создаёт новый только после галочки «Да, нужен ещё один табель»;
 * - состав: «Добавить людей после создания» или копия другого табеля — после создания (`rows/copy`), тост с
 *   итогом и «Отменить». До создания «Что будет» (T113): сколько людей в источнике и по какому правилу они
 *   добавятся — период ещё не начался: все (с ФИО); начался: только люди с проходами за период, остальные
 *   пропускаются. Точного предпросмотра до создания нет: `rows/copy` с `dryRun` работает у созданного табеля. Кого
 *   пропустили — тост после создания (предпросмотр копирования в новом табеле: пропущенные остаются вне табеля).
 *   Сбой связи при копировании — «Повторить» в тосте (T116).
 * Даты и период считает сервер заново: окно только помогает не ошибиться.
 */
export function CreateTimesheetDialog({
  open,
  onClose,
  brigades,
  sources,
  today,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  /** Бригады бригадира (`/api/me`): несколько — выбор, одна — без выбора, нет — создать табель нельзя (T131). */
  brigades?: readonly Brigade[];
  /** Табели, из которых можно скопировать состав. */
  sources: readonly TimesheetListItem[];
  /** Сегодня по времени объекта (`YYYY-MM-DD`): «Период ещё не начался». */
  today: string;
  /** Табель создан (и состав скопирован, если выбран): страница открывает его. */
  onCreated: (id: number) => void;
}) {
  const qc = useQueryClient();
  const { showError, showInfo } = useToast();
  /** Новый табель, в который скопирован состав: «Отменить» в тосте перечитывает его страницу (T102). */
  const copiedTo = useRef<number | null>(null);
  const undo = useUndo({
    screen: SCREEN,
    onDone: (res) => {
      if (copiedTo.current !== null) refreshTimesheet(qc, copiedTo.current, res.timesheet);
      else void qc.invalidateQueries({ queryKey: ['timesheets'] });
    },
  });
  const ids = useId();
  const list = brigades ?? [];
  const none = list.length === 0;
  const choose = list.length > 1;
  const only = list.length === 1 ? list[0] : undefined;

  const [brigadeId, setBrigadeId] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [copyFrom, setCopyFrom] = useState('');
  const [again, setAgain] = useState(false);
  const [busy, setBusy] = useState(false);
  /** Пересечение, о котором сервер сообщил при создании (`409 PERIOD_OVERLAP`). */
  const [late, setLate] = useState<{ key: string; overlaps: TimesheetOverlap[] } | null>(null);

  // Окно открылось — поля с начала.
  useEffect(() => {
    if (!open) return;
    setBrigadeId('');
    setStart('');
    setEnd('');
    setCopyFrom('');
    setAgain(false);
    setLate(null);
  }, [open]);

  const chosen = choose ? list.find((b) => String(b.id) === brigadeId) : only;
  const brigadeName = chosen?.name.trim() ?? '';
  const brigadeOk = Boolean(chosen);
  const dates = checkDates(start, end, today);
  const datesOk = Boolean(start && end) && dates.error === null;
  const valid = brigadeOk && datesOk;

  const body: CreateBody = {
    brigadeName,
    periodStart: start,
    periodEnd: end,
    ...(chosen ? { brigadeId: chosen.id } : {}),
  };
  const key = JSON.stringify(body);
  const settledKey = useDebounced(key, CHECK_DELAY_MS);
  const settled = settledKey === key;
  const check = useQuery({
    queryKey: ['create-check', settledKey],
    queryFn: () => api.createCheck(JSON.parse(settledKey) as CreateBody),
    enabled: open && valid && settled,
    retry: false,
    staleTime: 30_000,
  });
  // Источник состава: ФИО его людей — для «Что будет» и тоста о пропущенных.
  const sourceId = copyFrom ? Number(copyFrom) : null;
  const sourceItem = sources.find((t) => t.id === sourceId);
  const sourceSheet = useQuery({
    ...sourceQuery(sourceId),
    enabled: open && sourceId !== null,
    retry: false,
  });
  const lateHere = late && late.key === key ? late.overlaps : [];
  const overlaps = lateHere.length > 0 ? lateHere : valid && settled ? (check.data?.overlaps ?? []) : [];
  // Другие даты или бригада — подтверждение снова нужно.
  // biome-ignore lint/correctness/useExhaustiveDependencies: сброс галочки при смене бригады или дат
  useEffect(() => setAgain(false), [key]);
  const checking = valid && (!settled || check.isFetching);
  const canCreate = valid && !checking && !busy && (overlaps.length === 0 || again);

  /**
   * Скопировать состав в созданный табель: итог для тоста и «Отменить». Пропущен кто-то — его ФИО по предпросмотру
   * копирования в новом табеле (пропущенные в табель не попали) и составу источника; не вышло — только число,
   * а сбой — в журнал (T126).
   */
  const copyInto = async (
    id: number,
    source: number,
  ): Promise<{ text: string; detail?: string; action?: ToastAction }> => {
    const res = await api.copyRows(id, source);
    copiedTo.current = id;
    refreshTimesheet(qc, id);
    const text = `Скопировано: ${res.added}${res.skipped ? `; пропущено ${res.skipped} — нет проходов за период` : ''}`;
    let detail: string | undefined;
    if (res.skipped > 0) {
      const [plan, sheet] = await Promise.all([
        api.copyPreview(id, source).catch((err: unknown) => {
          reportClientError(SCREEN, 'copyPreview', err);
          return null;
        }),
        // Сбой уже записан в журнал запросом состава (`sourceQuery`).
        qc
          .fetchQuery(sourceQuery(source))
          .catch(() => null),
      ]);
      const skipped = Array.isArray(plan?.skip) ? plan.skip.map((x) => x.personId) : [];
      const nameOf = (pid: string) => sheet?.rows.find((r) => r.personId === pid)?.displayName ?? `№ ${pid}`;
      if (skipped.length > 0) detail = `Пропущены: ${nameList(skipped, nameOf)}.`;
    }
    return { text, detail, action: undo(res) };
  };
  /** Состав не скопирован: табель уже создан; сбой связи или сервера — «Повторить» копирует снова (T116). */
  const copyFailed = (err: unknown, id: number, source: number) =>
    showError(err, {
      screen: SCREEN,
      action: 'copyRows',
      lead: 'Состав не скопирован',
      note: 'Табель создан. Состав можно скопировать в «Добавить людей».',
      retry: isTransient(err) ? () => void copyAgain(id, source) : undefined,
    });
  const copyAgain = async (id: number, source: number) => {
    try {
      const c = await copyInto(id, source);
      showInfo(c.text, { action: c.action, detail: c.detail });
    } catch (err) {
      copyFailed(err, id, source);
    }
  };

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!canCreate) return;
    setBusy(true);
    try {
      const created = await api.create({ ...body, ...(overlaps.length > 0 ? { confirmOverlap: true } : {}) });
      void qc.invalidateQueries({ queryKey: ['timesheets'] });
      let text = `Табель «${created.brigadeName}» за ${range(created.periodStart, created.periodEnd)} создан`;
      let action: ToastAction | undefined;
      let detail: string | undefined;
      if (sourceId !== null) {
        try {
          const c = await copyInto(created.id, sourceId);
          text += `. ${c.text}`;
          action = c.action;
          detail = c.detail;
        } catch (err) {
          copyFailed(err, created.id, sourceId);
        }
      }
      showInfo(text, { action, detail });
      onCreated(created.id);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'PERIOD_OVERLAP') {
        setLate({ key, overlaps: overlapsOf(err.details) });
        setAgain(false);
      } else {
        showError(err, {
          screen: SCREEN,
          action: 'create',
          lead: 'Табель не создан',
          retry: () => void submit(),
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const datesMsgId = `${ids}-dates`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Новый табель"
      description="Табель — на одну бригаду и период до 31 дня. Часы заполнятся по проходам СКУД."
    >
      <form onSubmit={(e) => void submit(e)} noValidate>
        {none ? (
          <div data-no-brigade className="mb-4">
            <Callout tone="warn" icon={TriangleAlert}>
              У вас нет бригады на объекте — обратитесь к администратору
            </Callout>
          </div>
        ) : choose ? (
          <Field label="Бригада">
            <select className={inputCls} value={brigadeId} onChange={(e) => setBrigadeId(e.target.value)}>
              <option value="">Выберите бригаду</option>
              {list.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </Field>
        ) : (
          <div className="mb-4">
            <span className="mb-1.5 block text-[13px] font-medium text-ink">Бригада</span>
            <p className="text-[14px] font-semibold text-ink">{only?.name}</p>
          </div>
        )}

        {!none && (
          <>
            <div data-dates className="grid grid-cols-2 gap-3 max-sm:grid-cols-1 max-sm:gap-0">
              {(
                [
                  ['Начало', start, setStart],
                  ['Конец', end, setEnd],
                ] as const
              ).map(([label, value, set]) => (
                <label key={label} className="mb-2 block">
                  <span className="mb-1.5 block text-[13px] font-medium text-ink">{label}</span>
                  <input
                    className={`${inputCls} ${dates.error ? 'border-bad' : ''}`}
                    type="date"
                    value={value}
                    aria-invalid={dates.error ? true : undefined}
                    aria-describedby={datesMsgId}
                    onChange={(e) => set(e.target.value)}
                  />
                </label>
              ))}
            </div>
            <div id={datesMsgId} aria-live="polite" className="mb-4 text-[12.5px]">
              {dates.error ? (
                <span className="flex items-center gap-1.5 text-bad">
                  <TriangleAlert size={14} strokeWidth={1.75} aria-hidden />
                  {dates.error}
                </span>
              ) : (
                <span className="text-ink-3">{dates.hint}</span>
              )}
            </div>

            {overlaps.length > 0 && (
              <div
                data-overlap
                className="mb-4 flex items-start gap-2.5 rounded-control border border-warn/25 bg-warn-soft px-3.5 py-2.5 text-[13px] text-ink"
              >
                <TriangleAlert
                  size={16}
                  strokeWidth={1.75}
                  className="mt-px shrink-0 text-warn"
                  aria-hidden
                />
                <div className="min-w-0">
                  <p>
                    <b className="font-semibold">У бригады «{brigadeName}» уже есть табель на эти даты:</b>{' '}
                    {overlaps
                      .map((o) => `${range(o.periodStart, o.periodEnd)} (${statusText(o.status)})`)
                      .join(', ')}
                    . Один человек может попасть в два табеля — тогда по дню будет вопрос «Две бригады».
                  </p>
                  <label className="mt-1.5 flex min-h-10 cursor-pointer items-center gap-2 font-medium sm:min-h-8">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-accent"
                      checked={again}
                      onChange={(e) => setAgain(e.target.checked)}
                    />
                    Да, нужен ещё один табель
                  </label>
                </div>
              </div>
            )}

            {sources.length > 0 && (
              <Field label="Состав">
                <select className={inputCls} value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}>
                  <option value="">Добавить людей после создания</option>
                  {sources.map((t) => (
                    <option key={t.id} value={t.id}>
                      {`Скопировать из «${t.brigadeName}» ${range(t.periodStart, t.periodEnd)}`}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            {sourceItem && (
              <CopyPlan
                source={sourceItem}
                names={sourceSheet.data?.rows.map((r) => r.displayName)}
                dates={datesOk ? { start, end } : null}
                today={today}
              />
            )}
          </>
        )}

        <div data-actions className="flex flex-wrap justify-end gap-2 pt-1 max-sm:[&>button]:flex-auto">
          <Button onClick={onClose}>Отмена</Button>
          <Button type="submit" variant="primary" disabled={!canCreate}>
            Создать табель
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * «Что будет» с составом до создания (T113; FR-033, FR-041): сколько людей в табеле-источнике и по какому правилу они
 * добавятся. Период ещё не начался — все, с ФИО; начался — только люди с проходами за период, остальные пропускаются,
 * их называет тост после создания. Дат нет — правило зависит от них.
 */
function CopyPlan({
  source,
  names,
  dates,
  today,
}: {
  source: TimesheetListItem;
  /** ФИО людей источника; ещё не загружены — `undefined`. */
  names: readonly string[] | undefined;
  dates: { start: string; end: string } | null;
  today: string;
}) {
  const n = names?.length ?? source.peopleCount;
  const head = `В табеле «${source.brigadeName}» ${range(source.periodStart, source.periodEnd)}${n !== undefined ? `: ${people(n)}` : ''}.`;
  let rule: string;
  if (n === 0) rule = 'Людей в нём нет — копировать некого.';
  else if (!dates) rule = 'Укажите даты: от них зависит, кто добавится.';
  else if (dates.start > today) {
    const list = names && names.length > 0 ? `: ${nameList(names, (x) => x)}` : '';
    rule = `Период ещё не начался — добавятся все${n !== undefined ? ` ${n}` : ''}${list}.`;
  } else {
    rule = `Добавятся только люди с проходами за ${range(dates.start, dates.end)}. Остальные будут пропущены. Кого пропустили, покажем после создания.`;
  }
  return (
    <section
      aria-label="Что будет"
      aria-live="polite"
      data-copy-plan
      className="-mt-2 mb-4 rounded-control border border-line bg-subtle px-3.5 py-2.5 text-[13px] text-ink"
    >
      <h3 className="mb-1 text-[12px] font-medium text-ink-3">Что будет</h3>
      <p>{head}</p>
      <p className="mt-0.5 text-ink-2">{rule}</p>
    </section>
  );
}
